<script setup lang="ts">
  import { BaseButton } from '@qic/ui-components'
  import { computed, onMounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { ERROR_CODES, parseCustomError } from '@/shared/api/errorHandler'
  import FlowCard from '@/shared/components/FlowCard/FlowCard.vue'
  import WhatsAppButton from '@/entities/Flow/components/WhatsAppButton/WhatsAppButton.vue'
  import RepeatIcon from '@/shared/components/icons/RepeatIcon.vue'
  import HotlineButton from '@/entities/Flow/components/HotlineButton/HotlineButton.vue'
  import { GeneralRoutes } from '@/shared/types/router.types'
  import { useCommonStore } from '@/shared/store'
  import { CONTACTS_KEYS } from '@/shared/types/common.types'

  // TODO: Separate the logic of message selection from UI.
  interface Props {
    homeUrl: string
    message?: string
  }

  interface Emits {
    (e: 'resolve'): void
  }

  const props = withDefaults(defineProps<Props>(), {
    homeUrl: GeneralRoutes.NOT_FOUND,
  })

  const emit = defineEmits<Emits>()

  const { t } = useI18n()

  const customError = computed(() => parseCustomError(props.message ?? ''))
  const isProcessingQuoteError = computed(
    () => customError.value && customError.value.code === ERROR_CODES.processing_quote
  )

  const errorInfo = computed((): { title: string; desc: string } => {
    const defaultMessage = {
      title: t('qic_base_error_heder_line'),
      desc: t('qic_base_error_message'),
    }
    if (!customError.value) return defaultMessage

    if (isProcessingQuoteError.value) {
      return {
        title: t('qic.error.processing_quote_error.title', {
          quoteno: customError.value.info.quoteno,
        }),
        desc: t('qic.error.processing_quote_error.desc'),
      }
    }
    return defaultMessage
  })

  const commonStore = useCommonStore()

  const phoneLink = computed(() => commonStore.getContactValue(CONTACTS_KEYS.PHONE_LINK))
  const phoneText = computed(() => commonStore.getContactValue(CONTACTS_KEYS.PHONE_LINK_TEXT))
  const waLink = computed(() => commonStore.getContactValue(CONTACTS_KEYS.WA_LINK_GENERAL))

  onMounted(() => commonStore.getContacts())
</script>

<template>
  <FlowCard :with-button="false">
    <div class="network-error">
      <div class="network-error__content">
        <div class="network-error__message">
          <h1 class="network-error__message-title">
            {{ errorInfo.title }}
          </h1>
          <p class="network-error__message-desc">
            {{ errorInfo.desc }}
          </p>
        </div>
        <BaseButton
          v-if="!isProcessingQuoteError"
          icon="none"
          class="network-error__reload-btn"
          medium
          @click="emit('resolve')"
        >
          <RepeatIcon />
          {{ $t('qic_error_try_again') }}
        </BaseButton>
        <a :href="homeUrl">
          <BaseButton icon="none" small ghost>
            {{ $t('qic_error_go_home') }}
          </BaseButton>
        </a>
      </div>
      <div class="network-error__help">
        <p class="network-error__help-info">
          {{ $t('qic_base_error_phone_header') }}
        </p>
        <p class="network-error__help-contact">
          {{ $t('qic_base_error_contact_us') }}
        </p>
        <WhatsAppButton :href="waLink" />
        <HotlineButton :phone="phoneText" :link="phoneLink" />
      </div>
    </div>
  </FlowCard>
</template>

<style scoped lang="scss">
  @import './FlowNetworkErrorWidget';
</style>
